import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNotification, NOTIFICATION_TYPES } from '../contexts/NotificationContext';

const PendingUsers = () => {
  const { t } = useTranslation();
  const { getPendingUsers, approveUser, rejectUser, getAllUsers } = useAuth();
  const { addNotification } = useNotification();

  const [pendingUsers, setPendingUsers] = useState([]);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadPendingUsers();
  }, []);

  const loadPendingUsers = async () => {
    try {
      const pending = await getPendingUsers();
      setPendingUsers(pending);
    } catch (err) {
      console.error('Failed to load pending users');
    }
  };

  const handleApprove = async (userId, userName) => {
    try {
      await approveUser(userId);
      setMessage(t('admin.success.approved', { name: userName }));
      await loadPendingUsers();
      setTimeout(() => setMessage(''), 3000);

      addNotification(
        userId,
        NOTIFICATION_TYPES.APPROVAL,
        t('notification.messages.accountApproved'),
        t('notification.messages.accountApproved'),
        '/profile'
      );
    } catch (err) {
      setMessage(t('admin.error.approveFailed'));
    }
  };

  const handleReject = async (userId, userName) => {
    if (window.confirm(t('admin.confirm.reject', { name: userName }))) {
      try {
        addNotification(
          userId,
          NOTIFICATION_TYPES.APPROVAL,
          t('notification.messages.accountRejected'),
          t('notification.messages.accountRejected'),
          null
        );

        await rejectUser(userId);
        setMessage(t('admin.success.rejected', { name: userName }));
        await loadPendingUsers();
        setTimeout(() => setMessage(''), 3000);
      } catch (err) {
        setMessage(t('admin.error.rejectFailed'));
      }
    }
  };

  return (
    <div className="p-6">
      {message && (
        <div className={`py-4 px-5 rounded-lg mb-6 text-sm font-medium flex items-center gap-2.5 ${
          message.includes('error') || message.includes('실패')
            ? 'bg-red-50 text-red-600 border border-red-200'
            : 'bg-green-50 text-green-600 border border-green-200'
        }`}>
          {message}
        </div>
      )}

      <div className="mb-8">
        <h2 className="text-base font-bold mb-3.5 text-[#1d1d1f] tracking-tight pb-2.5 border-b border-gray-200">
          {t('admin.pending.title')}
        </h2>
        {pendingUsers.length === 0 ? (
          <p className="text-center text-gray-400 py-12 px-6 bg-neutral-50 rounded-xl text-[15px]">
            승인 대기 중인 회원이 없습니다.
          </p>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(340px,1fr))] gap-4 max-md:grid-cols-1">
            {pendingUsers.map((u) => (
              <div key={u.id} className="bg-amber-50 border border-amber-300 rounded-xl p-5 flex justify-between items-center gap-4">
                <div className="flex flex-col gap-1">
                  <strong className="text-[15px] font-semibold text-[#1d1d1f]">{u.name}</strong>
                  <span className="text-sm text-gray-600">{u.email}</span>
                  <span className="text-[13px] text-gray-400">{t('admin.pending.appliedOn')} {u.joinDate}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleApprove(u.id, u.name)}
                    className="py-2.5 px-4 bg-green-600 text-white border-none rounded-md text-[13px] font-semibold cursor-pointer transition-all hover:bg-green-700"
                  >
                    {t('admin.pending.approve')}
                  </button>
                  <button
                    onClick={() => handleReject(u.id, u.name)}
                    className="py-2.5 px-4 bg-transparent text-red-600 border border-red-600 rounded-md text-[13px] font-semibold cursor-pointer transition-all hover:bg-red-50"
                  >
                    {t('admin.pending.reject')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingUsers;
